import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import axios from '../../lib/axios'
import { ArrowLeft, ArrowRight, User, Mail, Server, CheckCircle, AlertCircle } from 'lucide-react'
import '../Pages.css'

export default function IMAPConnector() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [dnsValidation, setDnsValidation] = useState(null)
  const [showDnsWarning, setShowDnsWarning] = useState(false)
  const [showDnsConfirmDialog, setShowDnsConfirmDialog] = useState(false)
  const [pendingConnection, setPendingConnection] = useState(false)

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    imapUsername: '',
    imapPassword: '',
    imapHost: '',
    imapPort: '993',
    smtpUsername: '',
    smtpPassword: '',
    smtpHost: '',
    smtpPort: '587'
  })

  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email)
  const isValidPort = (port) => /^\d+$/.test(port) && parseInt(port) > 0 && parseInt(port) <= 65535

  const canGoNext1 =
    formData.firstName.trim().length > 0 &&
    formData.lastName.trim().length > 0 &&
    isValidEmail(formData.email)

  const canGoNext2 =
    formData.imapUsername.trim().length > 0 &&
    formData.imapPassword.trim().length > 0 &&
    formData.imapHost.trim().length > 0 &&
    isValidPort(formData.imapPort)

  const canConnect =
    formData.smtpUsername.trim().length > 0 &&
    formData.smtpPassword.trim().length > 0 &&
    formData.smtpHost.trim().length > 0 &&
    isValidPort(formData.smtpPort)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const goToStep2 = () => {
    if (!canGoNext1) return
    setError('')
    if (!formData.imapUsername) {
      setFormData({ ...formData, imapUsername: formData.email })
    }
    setStep(2)
  }

  const checkDNSRecords = async () => {
    const domain = formData.email.split('@')[1]?.toLowerCase()

    // Skip DNS check for Gmail
    if (domain === 'gmail.com' || domain === 'googlemail.com') {
      return { isGoogle: true, validation_passed: true, allRecordsValid: true }
    }

    try {
      // Call the new DNS verification API
      const res = await axios.post(`${import.meta.env.VITE_API_URL}dns/check`, {
        email: formData.email,
        mail_server_ip: null // Optional, will be auto-detected
      })

      const data = res.data

      // Check if all critical records exist
      const allRecordsValid =
        data.spf?.exists &&
        data.dkim?.exists &&
        data.dmarc?.exists &&
        data.mx?.exists

      // Collect warnings/issues
      const warnings = []
      if (!data.spf?.exists) warnings.push('SPF record is missing')
      else if (data.spf?.issues?.length > 0) warnings.push(...data.spf.issues)

      if (!data.dkim?.exists) warnings.push('DKIM record is missing')
      else if (data.dkim?.issues?.length > 0) warnings.push(...data.dkim.issues)

      if (!data.dmarc?.exists) warnings.push('DMARC record is missing')
      else if (data.dmarc?.issues?.length > 0) warnings.push(...data.dmarc.issues)

      if (!data.mx?.exists) warnings.push('MX records are missing')
      else if (data.mx?.issues?.length > 0) warnings.push(...data.mx.issues)

      if (data.ptr && !data.ptr?.exists) warnings.push('PTR record is missing')
      else if (data.ptr?.issues?.length > 0) warnings.push(...data.ptr.issues)

      return {
        validation_passed: allRecordsValid,
        allRecordsValid,
        warnings,
        results: data,
        score: data.score?.score ?? data.score,
        scoreDetails: data.score,
        recommendations: data.recommendations
      }
    } catch (err) {
      console.error('DNS check failed:', err)
      return null
    }
  }

  const testIMAPAndGoToStep3 = async () => {
    if (!canGoNext2) return

    setLoading(true)
    setError('')
    setDnsValidation(null)
    setShowDnsWarning(false)

    try {
      const imapPortNum = parseInt(formData.imapPort)
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        imap: {
          username: formData.imapUsername,
          password: formData.imapPassword,
          host: formData.imapHost,
          port: imapPortNum,
          use_ssl: imapPortNum === 993,
          use_starttls: imapPortNum === 143
        }
      }

      const res = await axios.post('email/test-imap', payload)

      if (res.data?.success) {
        if (!formData.smtpUsername) {
          setFormData({ ...formData, smtpUsername: formData.imapUsername || formData.email })
        }
        setStep(3)
      } else {
        setError(res.data?.message || 'IMAP test failed. Check credentials and host.')
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'IMAP connection failed'
      )
    } finally {
      setLoading(false)
    }
  }

  const proceedWithConnection = async () => {
    setLoading(true)
    setError('')
    setShowDnsConfirmDialog(false)

    try {
      const imapPortNum = parseInt(formData.imapPort)
      const smtpPortNum = parseInt(formData.smtpPort)

      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        imap: {
          username: formData.imapUsername,
          password: formData.imapPassword,
          host: formData.imapHost,
          port: imapPortNum,
          use_ssl: imapPortNum === 993,
          use_starttls: imapPortNum === 143
        },
        smtp: {
          username: formData.smtpUsername,
          password: formData.smtpPassword,
          host: formData.smtpHost,
          port: smtpPortNum,
          use_ssl: smtpPortNum === 465,
          use_tls: smtpPortNum === 587,
          reply_to_enabled: false
        }
      }

      const res = await axios.post('email/connect-any-provider', payload)

      if (res.status === 200 || res.data?.success) {
        navigate('/email-accounts')
      } else {
        setError(res.data?.message || 'Connection failed')
      }
    } catch (err) {
      // Handle DNS validation errors from backend
      const detail = err.response?.data?.detail

      if (detail && typeof detail === 'object' && detail.error === 'DNS configuration incomplete') {
        // Format DNS error message
        const criticalIssues = detail.critical_issues?.join(', ') || ''
        const allWarnings = detail.all_warnings?.join(', ') || ''
        const recommendation = detail.recommendation || ''

        setError(
          `${detail.message}\n\nCritical Issues: ${criticalIssues}\n\nAll Issues: ${allWarnings}\n\n${recommendation}\n\nPlease configure your domain's DNS records before connecting.`
        )

        // Store DNS results for display
        if (detail.dns_results) {
          setDnsValidation({
            validation_passed: false,
            results: detail.dns_results,
            warnings: detail.all_warnings,
            recommendation: detail.recommendation
          })
          setShowDnsWarning(true)
        }
      } else {
        setError(
          typeof detail === 'string' ? detail :
          err.response?.data?.message ||
          err.message ||
          'Connection failed'
        )
      }
    } finally {
      setLoading(false)
      setPendingConnection(false)
    }
  }

  const handleConnect = async () => {
    if (!canConnect) return

    setLoading(true)
    setError('')

    try {
      // Check DNS records first
      const dnsResult = await checkDNSRecords()

      if (dnsResult && !dnsResult.isGoogle) {
        // If all DNS records are valid, proceed directly
        if (dnsResult.allRecordsValid) {
          setLoading(false)
          await proceedWithConnection()
        } else {
          // Show warning and confirmation dialog
          setDnsValidation(dnsResult)
          setShowDnsConfirmDialog(true)
          setPendingConnection(true)
          setLoading(false)
        }
      } else {
        // For Gmail or if DNS check failed, proceed directly
        setLoading(false)
        await proceedWithConnection()
      }
    } catch (err) {
      console.error('Error during connection:', err)
      setLoading(false)
      await proceedWithConnection()
    }
  }

  return (
    <div className="page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>Connect via IMAP/SMTP</h2>
          <p>Any Email Provider</p>
        </div>
        <Link to="/email-accounts/add" className="btn btn-ghost">
          <ArrowLeft size={18} />
          <span>Back</span>
        </Link>
      </div>

      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        {/* Progress Indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '2rem',
          padding: '1rem',
          background: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius-lg)'
        }}>
          {[1, 2, 3].map((num, idx) => (
            <>
              <div key={num} style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: step >= num ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
                color: step >= num ? 'white' : 'var(--color-text-muted)',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                {step > num ? <CheckCircle size={18} /> : num}
              </div>
              {idx < 2 && (
                <div style={{
                  flex: 1,
                  height: '2px',
                  background: step > num ? 'var(--color-primary)' : 'var(--color-border)'
                }} />
              )}
            </>
          ))}
        </div>

        {/* Step 1: Profile */}
        {step === 1 && (
          <div className="card">
            <div className="card-header">
              <h3>
                <User size={20} />
                Profile Information
              </h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your-email@example.com"
                  />
                </div>
              </div>

              <button
                className="btn btn-primary"
                onClick={goToStep2}
                disabled={!canGoNext1}
                style={{ width: '100%', marginTop: '2rem' }}
              >
                <span>Continue to IMAP Settings</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: IMAP Settings */}
        {step === 2 && (
          <div className="card">
            <div className="card-header">
              <h3>
                <Mail size={20} />
                IMAP Settings (Incoming Mail)
              </h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {error && (
                <div style={{
                  padding: '1rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start'
                }}>
                  <AlertCircle size={18} style={{ color: '#ef4444', marginTop: '0.125rem', flexShrink: 0 }} />
                  <span style={{ color: '#dc2626', fontSize: '0.9rem' }}>{error}</span>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div className="form-group">
                  <label>IMAP Username *</label>
                  <input
                    type="text"
                    name="imapUsername"
                    value={formData.imapUsername}
                    onChange={handleChange}
                    placeholder="Usually your email address"
                  />
                </div>

                <div className="form-group">
                  <label>IMAP Password *</label>
                  <input
                    type="password"
                    name="imapPassword"
                    value={formData.imapPassword}
                    onChange={handleChange}
                    placeholder="Your email password"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>IMAP Host *</label>
                    <input
                      type="text"
                      name="imapHost"
                      value={formData.imapHost}
                      onChange={handleChange}
                      placeholder="imap.gmail.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>IMAP Port *</label>
                    <input
                      type="number"
                      name="imapPort"
                      value={formData.imapPort}
                      onChange={handleChange}
                      placeholder="993"
                    />
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem', display: 'block' }}>
                      993 (SSL) or 143 (STARTTLS)
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                <button
                  className="btn btn-ghost"
                  onClick={() => setStep(1)}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  <ArrowLeft size={18} />
                  <span>Back</span>
                </button>
                <button
                  className="btn btn-primary"
                  onClick={testIMAPAndGoToStep3}
                  disabled={!canGoNext2 || loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Testing IMAP...' : 'Test & Continue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: SMTP Settings */}
        {step === 3 && (
          <div className="card">
            <div className="card-header">
              <h3>
                <Server size={20} />
                SMTP Settings (Outgoing Mail)
              </h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {error && (
                <div style={{
                  padding: '1rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start'
                }}>
                  <AlertCircle size={18} style={{ color: '#ef4444', marginTop: '0.125rem', flexShrink: 0 }} />
                  <span style={{ color: '#dc2626', fontSize: '0.9rem', whiteSpace: 'pre-line' }}>{error}</span>
                </div>
              )}

              {showDnsWarning && dnsValidation && dnsValidation.warnings?.length > 0 && (
                <div style={{
                  padding: '1rem',
                  background: 'rgba(234, 179, 8, 0.1)',
                  border: '1px solid rgba(234, 179, 8, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <AlertCircle size={18} style={{ color: '#eab308', marginTop: '0.125rem', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#ca8a04', marginBottom: '0.5rem' }}>
                        DNS Configuration Warning
                      </h4>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#a16207', marginBottom: '0.75rem' }}>
                        Some DNS records are missing. This may affect email deliverability:
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.85rem', color: '#a16207' }}>
                        {dnsValidation.warnings.map((warning, idx) => (
                          <li key={idx} style={{ marginBottom: '0.25rem' }}>{warning}</li>
                        ))}
                      </ul>
                      {dnsValidation.recommendation && (
                        <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.85rem', color: '#a16207', fontStyle: 'italic' }}>
                          💡 {dnsValidation.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{
                    marginTop: '0.75rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid rgba(234, 179, 8, 0.2)',
                    fontSize: '0.8rem',
                    color: '#a16207'
                  }}>
                    <strong>DNS Records Status:</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <div>
                        {dnsValidation.results?.spf?.exists ? '✅' : '❌'} SPF Record
                      </div>
                      <div>
                        {dnsValidation.results?.dkim?.exists ? '✅' : '❌'} DKIM Record
                      </div>
                      <div>
                        {dnsValidation.results?.dmarc?.exists ? '✅' : '❌'} DMARC Record
                      </div>
                      <div>
                        {dnsValidation.results?.mx?.exists ? '✅' : '❌'} MX Records
                      </div>
                      <div>
                        {dnsValidation.results?.ptr?.exists ? '✅' : '❌'} PTR Record
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDnsWarning(false)}
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.35rem 0.75rem',
                      fontSize: '0.8rem',
                      background: 'rgba(234, 179, 8, 0.2)',
                      border: '1px solid rgba(234, 179, 8, 0.4)',
                      borderRadius: 'var(--radius-sm)',
                      color: '#ca8a04',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    Dismiss Warning
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div className="form-group">
                  <label>SMTP Username *</label>
                  <input
                    type="text"
                    name="smtpUsername"
                    value={formData.smtpUsername}
                    onChange={handleChange}
                    placeholder="Usually your email address"
                  />
                </div>

                <div className="form-group">
                  <label>SMTP Password *</label>
                  <input
                    type="password"
                    name="smtpPassword"
                    value={formData.smtpPassword}
                    onChange={handleChange}
                    placeholder="Your email password"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>SMTP Host *</label>
                    <input
                      type="text"
                      name="smtpHost"
                      value={formData.smtpHost}
                      onChange={handleChange}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>SMTP Port *</label>
                    <input
                      type="number"
                      name="smtpPort"
                      value={formData.smtpPort}
                      onChange={handleChange}
                      placeholder="587"
                    />
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem', display: 'block' }}>
                      587 (STARTTLS) or 465 (SSL)
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                <button
                  className="btn btn-ghost"
                  onClick={() => setStep(2)}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  <ArrowLeft size={18} />
                  <span>Back</span>
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleConnect}
                  disabled={!canConnect || loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Connecting...' : 'Connect Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* DNS Confirmation Dialog */}
      {showDnsConfirmDialog && dnsValidation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'var(--color-bg-primary)',
            borderRadius: 'var(--radius-lg)',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--color-border)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <AlertCircle size={24} style={{ color: '#eab308', flexShrink: 0 }} />
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                  DNS Configuration Warning
                </h3>
              </div>
            </div>

            <div style={{ padding: '1.5rem' }}>
              <p style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--color-text-secondary)' }}>
                Some DNS records are not properly configured. This may affect your email deliverability and cause emails to go to spam.
              </p>

              {/* DNS Records Status */}
              <div style={{
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '600' }}>
                  DNS Records Status:
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{dnsValidation.results?.spf?.exists ? '✅' : '❌'}</span>
                    <span style={{ fontSize: '0.9rem' }}>
                      SPF Record {dnsValidation.results?.spf?.exists ? '(Configured)' : '(Missing)'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{dnsValidation.results?.dkim?.exists ? '✅' : '❌'}</span>
                    <span style={{ fontSize: '0.9rem' }}>
                      DKIM Record {dnsValidation.results?.dkim?.exists ? '(Configured)' : '(Missing)'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{dnsValidation.results?.dmarc?.exists ? '✅' : '❌'}</span>
                    <span style={{ fontSize: '0.9rem' }}>
                      DMARC Record {dnsValidation.results?.dmarc?.exists ? '(Configured)' : '(Missing)'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{dnsValidation.results?.mx?.exists ? '✅' : '❌'}</span>
                    <span style={{ fontSize: '0.9rem' }}>
                      MX Records {dnsValidation.results?.mx?.exists ? '(Configured)' : '(Missing)'}
                    </span>
                  </div>
                  {dnsValidation.results?.ptr && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{dnsValidation.results?.ptr?.exists ? '✅' : '❌'}</span>
                      <span style={{ fontSize: '0.9rem' }}>
                        PTR Record {dnsValidation.results?.ptr?.exists ? '(Configured)' : '(Missing)'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Warnings List */}
              {dnsValidation.warnings?.length > 0 && (
                <div style={{
                  background: 'rgba(234, 179, 8, 0.1)',
                  border: '1px solid rgba(234, 179, 8, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#ca8a04' }}>
                    Issues Found:
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.85rem', color: '#a16207' }}>
                    {dnsValidation.warnings.map((warning, idx) => (
                      <li key={idx} style={{ marginBottom: '0.25rem' }}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {dnsValidation.recommendations?.length > 0 && (
                <div style={{
                  background: 'var(--color-bg-secondary)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '600' }}>
                    Recommendations:
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {dnsValidation.recommendations.slice(0, 3).map((rec, idx) => (
                      <div key={idx} style={{
                        padding: '0.75rem',
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--color-border)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            background: rec.priority === 'critical' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(234, 179, 8, 0.1)',
                            color: rec.priority === 'critical' ? '#dc2626' : '#ca8a04',
                            fontSize: '0.7rem',
                            borderRadius: 'var(--radius-sm)',
                            textTransform: 'uppercase',
                            fontWeight: '600'
                          }}>
                            {rec.priority}
                          </span>
                          <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>{rec.title}</span>
                        </div>
                        <p style={{ margin: '0.25rem 0', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                          {rec.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Score Display */}
              {dnsValidation.score !== undefined && (
                <div style={{
                  textAlign: 'center',
                  padding: '1rem',
                  background: 'var(--color-bg-secondary)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1rem'
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
                    Deliverability Score
                  </div>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: dnsValidation.score >= 80 ? '#10b981' : dnsValidation.score >= 60 ? '#eab308' : '#ef4444'
                  }}>
                    {dnsValidation.score}/100
                  </div>
                  {dnsValidation.scoreDetails?.grade && (
                    <div style={{
                      marginTop: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: 'var(--color-text-secondary)'
                    }}>
                      Grade: {dnsValidation.scoreDetails.grade}
                    </div>
                  )}
                </div>
              )}

              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginBottom: '1.5rem' }}>
                You can still proceed with the connection, but it's recommended to fix these DNS issues first for better email deliverability.
              </p>
            </div>

            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--color-border)',
              display: 'flex',
              gap: '0.75rem'
            }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowDnsConfirmDialog(false)
                  setPendingConnection(false)
                }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={proceedWithConnection}
                disabled={loading}
                style={{ flex: 1 }}
              >
                {loading ? 'Connecting...' : 'Proceed Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
